import type { ReactElement } from "react";
import { Navigate, useParams } from "react-router-dom";

import { GetWSMgr } from "@/Utils/StateLib";
import type { QPermissions } from "@coreapp/CoreApp/QPermissions";

type PermissionCheck = (
  perms: QPermissions,
  params: Record<string, string | undefined>,
  wsMgr: unknown
) => boolean;

interface PermissionGateProps {
  /** Evaluated synchronously at render. Return true to allow the child
   *  to render; false to redirect the user to their safe default. */
  check: PermissionCheck;
  children: ReactElement;
}

/**
 * Route-level permission wrapper. Place around the `element` of any
 * `<Route>` whose path should be gated by a QPermissions predicate:
 *
 *   <Route path="/plan/:planId" element={
 *     <PermissionGate check={perms => perms.canAccessPlanning()}>
 *       <CanvasElement />
 *     </PermissionGate>
 *   } />
 *
 * Single place for path-permission enforcement. QPermissions (on
 * wsMgr.fPermissions) is the source of truth. AppLayout gates <Outlet/>
 * on useAppReady, so permissions always have valid inputs here.
 *
 * On deny, redirects to the user's current server chart (if any),
 * else org default chart, else root — same priority as the legacy
 * redirectToSafeDefault in useUrlSecurityGate.
 */
export const PermissionGate = ({ check, children }: PermissionGateProps): ReactElement => {
  const params = useParams();
  const wsMgr = GetWSMgr();
  const perms = wsMgr?.fPermissions as QPermissions | undefined;
  if (perms && check(perms, params, wsMgr)) return children;
  return <SafeDefaultRedirect />;
};

const SafeDefaultRedirect = (): ReactElement => {
  const wsMgr = GetWSMgr();
  const doc = wsMgr?.GetDoc?.();
  if (doc?.IsServerChart?.()) {
    const chartId = doc.GetChartID?.();
    if (chartId != null) {
      return <Navigate to={`/base/${encodeURIComponent(String(chartId))}`} replace />;
    }
  }
  const orgInfo = wsMgr?.GetOrgInfo?.() as { defaultchart?: string | number } | null;
  const defaultChartId = orgInfo?.defaultchart;
  if (defaultChartId != null) {
    return <Navigate to={`/base/${encodeURIComponent(String(defaultChartId))}`} replace />;
  }
  return <Navigate to="/" replace />;
};
